import React from "react";
import { Stack, Typography } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  HubButton,
  HubHookFormInput,
  HubHookFormSelect,
  RobotSyncStatus,
} from "../../../components";
import { DipsTable } from "../../../tables";
import {
  isSealDip,
  findLastNonSealDipIndex,
} from "../../../tables/DipsTable/helper";
import { StyledHubCardStack } from "../styles";
import {
  useHighlightableField,
  useHubSnackBar,
  useGetPartData,
} from "../../../hooks";

const DIP_DEPARTMENT_ID = 2;

const gap = "10px";

export const PartDipDetails = ({
  basePath,
  defaults,
  partInstruction,
  routeOps,
  partId,
  locationId,
}) => {
  const routingPath = `${basePath}.routing`;
  const configPath = `${basePath}.config`;
  const notesPath = `${basePath}.notes`;

  const { getValues, setValue } = useFormContext();
  const { HighlightMenu, getFieldProps } = useHighlightableField();
  const { addSnackbarSuccess, addSnackbarError } = useHubSnackBar();

  // WO context only: partId/locationId let us pull the part's saved dip recipe.
  const { data: partData } = useGetPartData({ id: partId, locationId });
  const partDipInstruction = partData?.instructions?.find(
    (i) => i.departmentId === DIP_DEPARTMENT_ID,
  );

  const dipFieldOptions = useFieldArray({ name: routingPath });
  const { append, insert, remove, move, replace } = dipFieldOptions;

  const handleAdd = () => {
    const currentDips = getValues(routingPath) || [];
    const lastNonSealIndex = findLastNonSealDipIndex(currentDips);
    const template =
      lastNonSealIndex >= 0 ? currentDips[lastNonSealIndex] : null;

    const newDip = template
      ? { ...template, config: { ...(template.config || {}) }, id: null }
      : {
          stationId: null,
          leadTime: 1440,
          cycleTime: 300,
          config: {
            materialId: null,
            movementId: 1,
            slurryId: null,
            prewet: false,
          },
        };

    const lastDip = currentDips[currentDips.length - 1];
    const sealLast = isSealDip(lastDip);
    const addedAt = sealLast ? currentDips.length - 1 : currentDips.length;

    const sectionAdd = (stamped) =>
      sealLast ? insert(addedAt, stamped) : append(stamped);

    if (routeOps?.add)
      routeOps.add(basePath, addedAt, () => sectionAdd(newDip));
    else sectionAdd(newDip);
  };

  const tableMove = (start, end) => {
    if (routeOps?.move) routeOps.move(basePath, start, end, move);
    else move(start, end);
  };

  const tableRemove = (idx) => {
    if (routeOps?.remove) routeOps.remove(basePath, idx, remove);
    else remove(idx);
  };

  const handleSyncWithPart = () => {
    if (!partDipInstruction) {
      addSnackbarError({ message: "No part dip instructions found" });
      return;
    }

    // Overlay recipe onto existing rows so WO-only fields (dates, quantity,
    // progress, labor, id) survive; surplus dips clone the template like handleAdd.
    const currentDips = getValues(routingPath) || [];
    const templateIndex = findLastNonSealDipIndex(currentDips);
    const template = templateIndex >= 0 ? currentDips[templateIndex] : null;

    const applyRecipe = (base, recipeDip) => ({
      ...base,
      stationId: recipeDip.stationId ?? null,
      leadTime: recipeDip.leadTime ?? 1440,
      cycleTime: recipeDip.cycleTime ?? 0,
      config: {
        ...(base.config || {}),
        materialId: recipeDip.config?.materialId ?? null,
        movementId: recipeDip.config?.movementId ?? 1,
        slurryId: recipeDip.config?.slurryId ?? null,
        prewet: recipeDip.config?.prewet ?? false,
      },
    });

    const newDipBase = () =>
      template
        ? { ...template, config: { ...(template.config || {}) }, id: null }
        : { leadTime: 1440, cycleTime: 0, config: {} };

    const syncedDips = (partDipInstruction.routing || []).map((recipeDip, idx) =>
      applyRecipe(currentDips[idx] ?? newDipBase(), recipeDip),
    );

    replace(syncedDips);
    setValue(`${configPath}.weight`, partDipInstruction.config?.weight ?? 0, {
      shouldDirty: true,
    });
    setValue(
      `${configPath}.etching`,
      Number(Boolean(partDipInstruction.config?.etching)),
      { shouldDirty: true },
    );
    setValue(notesPath, partDipInstruction.notes || "", { shouldDirty: true });

    addSnackbarSuccess({ message: "Dip recipe synced with part" });
  };

  const config = partInstruction?.config || defaults?.config;

  return (
    <StyledHubCardStack>
      <Stack gap={gap}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" gap="12px">
            <Typography variant="boldCaptionMain">Dip Recipe</Typography>
            {partId && locationId && (
              <HubButton
                label="Sync with Part"
                variant="outlined"
                size="small"
                sx={{ fontSize: 10 }}
                onClick={handleSyncWithPart}
                disabled={!partDipInstruction}
              />
            )}
          </Stack>
          <RobotSyncStatus dipInstructions={config} />
        </Stack>
        <DipsTable
          {...dipFieldOptions}
          move={tableMove}
          remove={tableRemove}
          basePath={routingPath}
          getFieldProps={getFieldProps}
          locationId={getValues("locationId")}
        />
        <HubButton
          label="Add Dip"
          variant="text"
          sx={{ fontSize: 10, ml: "auto", mb: -5 }}
          onClick={handleAdd}
        />
      </Stack>
      <Stack direction="row" gap={gap}>
        <HubHookFormInput
          name={`${configPath}.weight`}
          label="Final Dip Weight"
          placeholder="ex. 4.55"
          endAdornment="lbs"
          isDecimal
          sx={{ width: "25%" }}
          {...getFieldProps(`${configPath}.weight`)}
        />
        <HubHookFormSelect
          name={`${configPath}.etching`}
          label="Etching Required"
          options={["False", "True"].map((label, id) => ({ id, label }))}
          {...getFieldProps(`${configPath}.etching`)}
        />
      </Stack>
      <Stack gap={gap}>
        <Typography variant="boldCaptionMain">Dip Notes</Typography>
        <HubHookFormInput
          name={notesPath}
          label="Special Instructions"
          placeholder="Dip notes..."
          rows={5}
          multiline
          {...getFieldProps(notesPath)}
        />
      </Stack>
      {HighlightMenu}
    </StyledHubCardStack>
  );
};

export default PartDipDetails;
